import { imgUrl } from '@/request/http'
import { getWebsiteScales } from '@/request/apis/website'
import carsCover from '@/assets/img/product/cars2-cover.png'
import adhdCover from '@/assets/img/product/adhd-rs5-cn-cover.png'
import carsThumb from '@/assets/design/product-thumb-cars2.png'
import adhdThumb from '@/assets/design/product-thumb-adhd.png'
import carsBanner from '@/assets/img/product/product-CARS-2.png'
import adhdBanner from '@/assets/img/product/product-ADHD-RS-5(CN).png'

let loadedLang = null
let cachedItems = []
let loadingLang = null
let loadingPromise = null

const KNOWN_TEMPLATE_KEYS = ['cars2', 'adhd']

const TEMPLATE_VISUALS = {
  cars2: {
    cover: carsCover,
    bannerThumb: carsThumb,
    bannerImage: carsBanner,
    headingColor: '#dd9318',
    subheadingColor: '#e19923',
    captionColor: '#d18a18',
    lineColor: '#ec9d16',
    ribbonColor: '#d8a04d'
  },
  adhd: {
    cover: adhdCover,
    bannerThumb: adhdThumb,
    bannerImage: adhdBanner,
    headingColor: '#e7b174',
    subheadingColor: '#335b95',
    captionColor: '#3e6298',
    lineColor: '#379192',
    ribbonColor: '#1d4f8f'
  }
}

const THEME_VISUALS = {
  gold: {
    headingColor: '#dd9318',
    subheadingColor: '#e19923',
    captionColor: '#d18a18',
    lineColor: '#ec9d16',
    ribbonColor: '#d8a04d'
  },
  blue: {
    headingColor: '#e7b174',
    subheadingColor: '#335b95',
    captionColor: '#3e6298',
    lineColor: '#379192',
    ribbonColor: '#1d4f8f'
  },
  orange: {
    headingColor: '#f08a24',
    subheadingColor: '#f59a36',
    captionColor: '#e87411',
    lineColor: '#ff9a2f',
    ribbonColor: '#ff7d12'
  },
  green: {
    headingColor: '#0b8d93',
    subheadingColor: '#0b8d93',
    captionColor: '#0b7378',
    lineColor: '#13a4a8',
    ribbonColor: '#0b8d93'
  }
}

const DEFAULT_RIBBON_COLOR = '#0B8D93'

export function normalizeThemeColor(value) {
  const color = String(value || '').trim().toUpperCase()
  return /^#[0-9A-F]{6}$/.test(color) ? color : ''
}

function hexToRgb(color) {
  const normalized = normalizeThemeColor(color)
  if (!normalized) return null
  return [1, 3, 5].map(index => parseInt(normalized.slice(index, index + 2), 16) / 255)
}

function relativeLuminance(color) {
  const rgb = hexToRgb(color)
  if (!rgb) return 0
  const channels = rgb.map(channel => channel <= 0.03928
    ? channel / 12.92
    : Math.pow((channel + 0.055) / 1.055, 2.4))
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]
}

export function getContrastRatio(background, foreground) {
  const first = relativeLuminance(background)
  const second = relativeLuminance(foreground)
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05)
}

export function getContrastingTextColor(background) {
  const color = normalizeThemeColor(background) || DEFAULT_RIBBON_COLOR
  return getContrastRatio(color, '#000000') >= getContrastRatio(color, '#FFFFFF')
    ? '#000000'
    : '#FFFFFF'
}

function resolveLegacyThemeVisual(source) {
  const themeColorKey = String(source.themeColor || '').trim().toLowerCase()
  const themeKey = String(source.themeKey || '').trim().toLowerCase()
  return THEME_VISUALS[themeColorKey] || THEME_VISUALS[themeKey] || null
}

const CATEGORY_LABELS = {
  neuro: {
    zh: '神经发育',
    en: 'Neurodevelopment'
  },
  social: {
    zh: '社交沟通',
    en: 'Social Communication'
  },
  motor: {
    zh: '运动能力',
    en: 'Motor Ability'
  }
}

const TYPE_LABELS = {
  diagnostic: {
    zh: '诊断量表',
    en: 'Diagnostic'
  },
  screening: {
    zh: '筛查量表',
    en: 'Screening'
  }
}

const KNOWN_SCALE_CATEGORIES = {
  cars2: ['neuro'],
  adhd: ['neuro'],
  tgmd3: ['motor']
}

function pickLang(zhValue, enValue, lang) {
  return lang === 'en' ? (enValue || zhValue || '') : (zhValue || enValue || '')
}

function normalizeText(value) {
  return `${value || ''}`.replace(/\s+/g, '').toLowerCase()
}

function slugify(value) {
  return normalizeText(value)
    .replace(/\(cn\)/g, 'cn')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function normalizeArray(value) {
  if (Array.isArray(value)) return value.filter(Boolean)
  if (!value) return []
  return `${value}`.split(',').map(item => item.trim()).filter(Boolean)
}

function normalizeScaleType(value) {
  return `${value || ''}`.toLowerCase()
}

function resolveScaleCategories(source, key, detailTemplateKey) {
  const categories = normalizeArray(source.categoryCodes || source.categories || source.category)
  if (categories.length) return categories
  return (KNOWN_SCALE_CATEGORIES[key] || KNOWN_SCALE_CATEGORIES[detailTemplateKey] || []).slice()
}

function buildScaleRequestParams(lang, filters = {}) {
  const params = { lang }
  if (filters.category && filters.category !== 'all') {
    params.category = filters.category
  }
  if (filters.scaleType && filters.scaleType !== 'all') {
    params.scaleType = filters.scaleType
  }
  if (filters.keyword) {
    params.keyword = filters.keyword
  }
  return params
}

function hasActiveScaleFilters(params) {
  return !!(params.category || params.scaleType || params.keyword)
}

function normalizeTemplateKey(value) {
  const key = `${value || 'none'}`.toLowerCase()
  return KNOWN_TEMPLATE_KEYS.indexOf(key) > -1 ? key : 'none'
}

function normalizeImage(value, fallback) {
  if (!value) return fallback || ''
  const path = `${value}`.trim()
  if (!path) return fallback || ''
  if (/^(https?:)?\/\//.test(path) || /^data:/.test(path)) return path

  const profilePath = path
    .replace(/^\/+/, '')
    .replace(/^profile\/?/, '')

  return `${imgUrl.replace(/\/+$/, '')}/${profilePath}`
}

function normalizeDetailUrl(value) {
  const url = `${value || ''}`.trim()
  if (!url) return ''
  if (/^(https?:)?\/\//i.test(url) || url.indexOf('#/') === 0) return url
  return url.charAt(0) === '/' ? `#${url}` : url
}

function buildDetailLink(item) {
  if (!item) return '#/product'
  const detailUrl = normalizeDetailUrl(item.detailUrl)
  if (detailUrl) return detailUrl
  if (item.detailTemplateKey === 'cars2' || item.detailTemplateKey === 'adhd') {
    return `#/product/detail?scale=${encodeURIComponent(item.detailTemplateKey)}`
  }
  const key = item.slug || item.sourceId || item.key || item.code
  return key ? `#/product?scale=${encodeURIComponent(key)}` : '#/product'
}

function buildMatchers(item) {
  return [
    item.code,
    item.slug,
    item.name,
    item.nameEn,
    item.shortName,
    item.shortNameEn,
    item.cardTitle,
    item.heroTitle,
    item.summary
  ].map(normalizeText).filter(Boolean)
}

export function normalizeWebsiteScale(item, lang = 'zh') {
  const source = item || {}
  const rawTemplateKey = `${source.detailTemplateKey || 'none'}`.toLowerCase()
  const sourceSortNum = source.sortNum !== undefined && source.sortNum !== null
    ? source.sortNum
    : source.sortNo
  const detailTemplateKey = normalizeTemplateKey(rawTemplateKey)
  const key = source.slug || slugify(source.code || source.name || source.id)
  const visual = TEMPLATE_VISUALS[detailTemplateKey] || resolveLegacyThemeVisual(source) || THEME_VISUALS.gold
  const explicitRibbonColor = normalizeThemeColor(source.themeColor)
  const legacyThemeVisual = resolveLegacyThemeVisual(source)
  const ribbonColor = explicitRibbonColor ||
    (legacyThemeVisual && legacyThemeVisual.ribbonColor) ||
    (TEMPLATE_VISUALS[detailTemplateKey] && TEMPLATE_VISUALS[detailTemplateKey].ribbonColor) ||
    DEFAULT_RIBBON_COLOR
  const name = pickLang(source.name || source.shortName || source.code, source.nameEn || source.shortNameEn, lang)
  const shortName = pickLang(source.shortName || source.code || source.name, source.shortNameEn || source.code || source.nameEn, lang)
  const summary = pickLang(source.summary, source.summaryEn, lang)
  const normalized = {
    id: source.id || key,
    key,
    sourceId: key,
    slug: source.slug || key,
    code: source.code || shortName || name,
    name,
    nameEn: source.nameEn || '',
    shortName,
    shortNameEn: source.shortNameEn || '',
    cardTitle: name,
    heroTitle: name,
    heroCaption: source.code || shortName || name,
    age: pickLang(source.applicableAge || source.ageText, source.applicableAgeEn || source.ageTextEn, lang),
    duration: pickLang(source.duration || source.durationText, source.durationEn || source.durationTextEn, lang),
    summary,
    categories: resolveScaleCategories(source, key, detailTemplateKey),
    type: normalizeScaleType(source.scaleType),
    scaleType: source.scaleType || '',
    themeKey: source.themeKey || '',
    detailTemplateKey,
    rawDetailTemplateKey: rawTemplateKey,
    detailScale: detailTemplateKey === 'none' ? key : detailTemplateKey,
    navVisible: source.navVisible !== false && `${source.navVisible}` !== '0',
    navSort: Number(source.navSort || 0),
    homeFeatured: source.homeFeatured === true || `${source.homeFeatured}` === '1',
    homeSort: Number(source.homeSort || 0),
    sortNum: Number(sourceSortNum || 0),
    cover: normalizeImage(source.coverImage, visual.cover),
    bannerThumb: normalizeImage(source.coverImage, visual.bannerThumb || visual.cover),
    bannerImage: normalizeImage(source.bannerImage, visual.bannerImage || visual.cover),
    headingColor: visual.headingColor,
    subheadingColor: visual.subheadingColor,
    captionColor: visual.captionColor,
    lineColor: visual.lineColor,
    ribbonColor,
    ribbonTextColor: getContrastingTextColor(ribbonColor),
    detailUrl: normalizeDetailUrl(source.detailUrl)
  }

  normalized.link = buildDetailLink(normalized)
  normalized.detailUrl = normalized.link
  normalized.matchers = buildMatchers(normalized)
  normalized.keywords = normalized.matchers.slice()
  return normalized
}

export function buildScaleLink(item) {
  return buildDetailLink(item)
}

export function getCategoryLabel(code, lang = 'zh') {
  const label = CATEGORY_LABELS[code]
  if (!label) return code || ''
  return pickLang(label.zh, label.en, lang)
}

export function getScaleTypeLabel(code, lang = 'zh') {
  const key = normalizeScaleType(code)
  const label = TYPE_LABELS[key]
  if (!label) return code || ''
  return pickLang(label.zh, label.en, lang)
}

function normalizeWebsiteScaleRows(rows, lang) {
  return rows
    .map(item => normalizeWebsiteScale(item, lang))
    .sort((a, b) => {
      const sortDelta = a.sortNum - b.sortNum
      if (sortDelta !== 0) return sortDelta
      return `${a.code}`.localeCompare(`${b.code}`)
    })
}

export function loadWebsiteCatalog(lang = 'zh', filters = {}) {
  const params = buildScaleRequestParams(lang, filters)
  if (hasActiveScaleFilters(params)) {
    return getWebsiteScales(params)
      .then(res => normalizeWebsiteScaleRows(Array.isArray(res.data) ? res.data : [], lang))
  }

  if (loadedLang === lang) return Promise.resolve(cachedItems)
  if (loadingPromise && loadingLang === lang) return loadingPromise

  loadingLang = lang
  loadingPromise = getWebsiteScales(params)
    .then(res => {
      const rows = Array.isArray(res.data) ? res.data : []
      cachedItems = normalizeWebsiteScaleRows(rows, lang)
      loadedLang = lang
      return cachedItems
    })
    .finally(() => {
      loadingLang = null
      loadingPromise = null
    })

  return loadingPromise
}

export function clearWebsiteCatalogCache() {
  loadedLang = null
  cachedItems = []
  loadingLang = null
  loadingPromise = null
}



// WEBPACK FOOTER //
// ./src/request/apis/websiteCatalog.js